import React, { useState, useEffect, useCallback } from "react";
import { Pagination } from "flowbite-react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Search,
  Users,
  Loader2,
  RefreshCw,
  KeyRound,
  X,
  Eye,
  EyeOff,
  ShieldCheck,
  AlertTriangle,
  CheckCircle,
  Building2,
} from "lucide-react";
import { getToken, getUserRole } from "../utils/auth";

export default function AdminUserManagement() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [resetModal, setResetModal] = useState({ open: false, user: null });
  const [assignModal, setAssignModal] = useState({ open: false, user: null });
  const isAdmin = getUserRole() === "Admin";

  // Debounce search — resets to page 1 when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUsers = useCallback(async () => {
    const params = { page: currentPage - 1, size: pageSize };
    if (debouncedSearch) params.search = debouncedSearch;
    const res = await axios.get(
      process.env.REACT_APP_BASE_URL + "hec/users/admin/all",
      { headers: { Authorization: `Bearer ${getToken()}` }, params }
    );
    return res.data;
  }, [currentPage, pageSize, debouncedSearch]);

  const {
    data: usersPage,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["adminUsers", currentPage, debouncedSearch],
    queryFn: fetchUsers,
    keepPreviousData: true,
    staleTime: 30 * 1000,
  });

  const users = usersPage?.content || [];
  const totalPages = usersPage?.totalPages || 1;
  const totalElements = usersPage?.totalElements || 0;

  const openResetModal = (user) => setResetModal({ open: true, user });
  const closeResetModal = () => setResetModal({ open: false, user: null });

  const handleResetSuccess = () => {
    closeResetModal();
    queryClient.invalidateQueries(["adminUsers"]);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <TopBarComponent />
      <div className="flex flex-1">
        <SidbarComponent />

        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-7 h-7 text-cyan-600" />
                  User Management
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Search for users and reset passwords on their behalf
                </p>
              </div>
              <button
                onClick={() => refetch()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-cyan-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Stats Card */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="text-cyan-600 mb-3">
                <Users className="w-5 h-5" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{totalElements}</p>
              <p className="text-xs font-medium mt-1 text-gray-500">Total Users</p>
            </div>
            {isAdmin && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <div className="text-amber-500 mb-3">
                  <KeyRound className="w-5 h-5" />
                </div>
                <p className="text-sm font-semibold text-gray-700">Admin Access</p>
                <p className="text-xs font-medium mt-1 text-gray-500">
                  You can reset passwords on behalf of users
                </p>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or national ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
              />
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <Loader2 className="w-10 h-10 animate-spin text-cyan-500 mb-3" />
                <p className="text-sm">Loading users...</p>
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
                <p className="text-sm font-medium text-red-600">Failed to load users</p>
                <button
                  onClick={() => refetch()}
                  className="mt-3 text-sm text-cyan-600 hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <Users className="w-10 h-10 mb-3" />
                <p className="text-sm font-medium">No users found</p>
                {debouncedSearch && (
                  <p className="text-xs mt-1">Try adjusting your search</p>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">National ID</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</th>
                        <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                        {isAdmin && (
                          <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {users.map((user, index) => {
                        const rowNum = (currentPage - 1) * pageSize + index + 1;
                        return (
                          <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-4 text-gray-400 text-xs font-mono">{rowNum}</td>
                            <td className="px-5 py-4">
                              <span className="font-semibold text-gray-900">
                                {user.firstName} {user.lastName}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-gray-600">{user.email || "—"}</td>
                            <td className="px-5 py-4">
                              <span className="font-mono text-gray-700">{user.nationalId || "—"}</span>
                            </td>
                            <td className="px-5 py-4 text-gray-600">{user.phoneNumber || "—"}</td>
                            <td className="px-5 py-4">
                              {user.roleName ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 border border-cyan-200">
                                  <ShieldCheck className="w-3 h-3" />
                                  {user.roleName}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs italic">No role</span>
                              )}
                            </td>
                            {isAdmin && (
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-2">
                                  {user.roleName === "HLI_ADMIN" && (
                                    <button
                                      onClick={() => setAssignModal({ open: true, user })}
                                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-lg hover:bg-cyan-100 transition-colors"
                                    >
                                      <Building2 className="w-3.5 h-3.5" />
                                      Assign Institution
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer: count + pagination */}
                <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-4 border-t border-gray-100 gap-3">
                  <p className="text-sm text-gray-500">
                    Showing{" "}
                    <span className="font-medium text-gray-700">
                      {(currentPage - 1) * pageSize + 1}–
                      {Math.min(currentPage * pageSize, totalElements)}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium text-gray-700">{totalElements}</span>{" "}
                    users
                  </p>
                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(p) => setCurrentPage(p)}
                      showIcons
                      theme={{
                        pages: {
                          selector: {
                            active: "bg-cyan-600 text-white border-cyan-600 hover:bg-cyan-700",
                          },
                        },
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <FooterComponents />

      {/* Reset Password Modal */}
      {resetModal.open && resetModal.user && (
        <ResetPasswordModal
          user={resetModal.user}
          onClose={closeResetModal}
          onSuccess={handleResetSuccess}
        />
      )}

      {/* Assign Institution Modal */}
      {assignModal.open && assignModal.user && (
        <AssignInstitutionModal
          user={assignModal.user}
          onClose={() => setAssignModal({ open: false, user: null })}
          onSuccess={() => {
            setAssignModal({ open: false, user: null });
            queryClient.invalidateQueries(["adminUsers"]);
          }}
        />
      )}
    </div>
  );
}

// ── Reset Password Modal ──────────────────────────────────────────────────────
function ResetPasswordModal({ user, onClose, onSuccess }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.warning("Password must be at least 6 characters.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warning("Passwords do not match.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}hec/users/admin/reset-password`,
        null,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          params: { userName: user.userName, newPassword },
        }
      );
      toast.success(
        `Password reset successfully for ${user.firstName} ${user.lastName}.`,
        { position: "top-center", autoClose: 4000 }
      );
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to reset password. Please try again.";
      toast.error(msg, { position: "top-center", autoClose: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Reset Password</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {user.firstName} {user.lastName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="px-6 pt-5 pb-2">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Verify User Identity
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-400">Email</p>
                <p className="font-medium text-gray-800 truncate">{user.email || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">National ID</p>
                <p className="font-mono font-medium text-gray-800">{user.nationalId || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Phone</p>
                <p className="font-medium text-gray-800">{user.phoneNumber || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Role</p>
                <p className="font-medium text-gray-800">{user.roleName || "—"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pt-4 pb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                className="w-full pr-10 pl-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Confirm Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
            />
          </div>

          {confirmPassword && newPassword !== confirmPassword && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Passwords do not match
            </p>
          )}

          {confirmPassword && newPassword === confirmPassword && newPassword.length >= 6 && (
            <p className="text-xs text-green-600 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Passwords match
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <KeyRound className="w-4 h-4" />
              )}
              {isSubmitting ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Assign Institution Modal ──────────────────────────────────────────────────
function AssignInstitutionModal({ user, onClose, onSuccess }) {
  const [selectedInstitutionId, setSelectedInstitutionId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: institutions = [], isLoading: loadingHlis } = useQuery({
    queryKey: ["allHli"],
    queryFn: async () => {
      const res = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/hli/getAllHli",
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedInstitutionId) {
      toast.warning("Please select an institution.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}hec/users/admin/assign-institution`,
        null,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          params: {
            userName: user.userName,
            institutionId: selectedInstitutionId,
          },
        }
      );
      const inst = institutions.find((i) => i.id === selectedInstitutionId);
      toast.success(
        `Institution "${inst?.fullName || "selected"}" assigned to ${user.firstName} ${user.lastName}.`,
        { position: "top-center", autoClose: 4000 }
      );
      onSuccess();
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to assign institution. Please try again.";
      toast.error(msg, { position: "top-center", autoClose: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-cyan-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-cyan-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Assign Institution</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {user.firstName} {user.lastName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="px-6 pt-5 pb-2">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              User Details
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-400">Email</p>
                <p className="font-medium text-gray-800 truncate">{user.email || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Role</p>
                <p className="font-medium text-gray-800">{user.roleName || "—"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pt-4 pb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Select Institution
            </label>
            {loadingHlis ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading institutions...
              </div>
            ) : (
              <select
                value={selectedInstitutionId}
                onChange={(e) => setSelectedInstitutionId(e.target.value)}
                required
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none bg-white"
              >
                <option value="">-- Select an institution --</option>
                {institutions.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.fullName} {inst.institutionCode ? `(${inst.institutionCode})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loadingHlis}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Building2 className="w-4 h-4" />
              )}
              {isSubmitting ? "Assigning..." : "Assign Institution"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
